/* ============================================================================
   Furniva mobile chrome — the header and bottom bar below 1024px.

   Two jobs:

   1. Get out of the way while reading. Scrolling down slides the header up and
      the bottom bar down; scrolling up brings both back. The state is one class
      on <html>, and every movement is a CSS transition on transform, so it runs
      on the compositor and never moves layout.

   2. "Deliver to". The header shows the pincode this device last checked and
      the district the delivery master returned for it; tapping it opens a
      sheet that asks the Worker — the same endpoint and the same answer as the
      product page. localStorage key and event are shared with
      furniva-pincode.js, so a pincode entered anywhere is the one shown
      everywhere.
   ============================================================================ */
(function () {
  'use strict';

  var root = document.documentElement;
  var mobile = window.matchMedia('(max-width: 1024px)');

  /* -------------------------------------------------------- hide on scroll -- */
  /* Thresholds, not every pixel. A few pixels of jitter from a thumb resting
     on the glass must not flick the chrome, so direction only counts once it
     has travelled DELTA; near the top the chrome is always shown, because the
     header is where the page starts; and at the very end it comes back, so the
     last thing on the page is never a dead end without navigation. */
  var DELTA = 8;
  var TOP_ZONE = 64;
  var lastY = window.scrollY || 0;
  var hidden = false;
  var ticking = false;

  function blocked() {
    // A drawer, the search page or the pincode sheet is open: the chrome that
    // opened it stays where it is.
    return root.classList.contains('fv-chrome-lock') ||
      document.querySelector('.fv-drawer.is-open, .fv-search:not([hidden]), dialog[open]');
  }

  function set(next) {
    if (next === hidden) return;
    hidden = next;
    root.classList.toggle('fv-chrome-hidden', hidden);
  }

  function update() {
    ticking = false;
    if (!mobile.matches) { set(false); return; }
    var y = window.scrollY || 0;
    // Within a bar's height of the end counts as the end. A phone's toolbar
    // collapses and expands as it scrolls, so innerHeight changes under the
    // thumb and an exact comparison could miss the bottom altogether; the
    // visual viewport is the height actually on screen.
    var viewH = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    var atEnd = Math.max(viewH, window.innerHeight) + y >= document.documentElement.scrollHeight - 72;
    if (y <= TOP_ZONE || atEnd || blocked()) {
      set(false);
    } else if (Math.abs(y - lastY) >= DELTA) {
      set(y > lastY);
    } else {
      return; // too small a move to count; keep the reference point
    }
    lastY = y;
  }

  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }, { passive: true });

  // Keyboard focus into either bar brings it back: a hidden control that has
  // focus is a control nobody can see.
  document.addEventListener('focusin', function (event) {
    if (event.target.closest && event.target.closest('.fv-mhead, .fv-bottom-nav')) set(false);
  });

  if (mobile.addEventListener) mobile.addEventListener('change', function () { set(false); });

  /* ---------------------------------------------------------- tap motion -- */
  /* Home and Saved answer the tap with a small motion (CSS, .is-tapped). The
     navigation is never held back for it: the current page stays on screen
     until the next one is ready, and the motion plays in that time, so the
     tap is acknowledged without adding a moment to anyone's wait.

     Registered on document in the bubbling phase and stepping aside for any
     click already handled — the wishlist code may route Saved itself — and for
     a modified click, which opens a new tab. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('.fv-bottom-nav__home, .fv-bottom-nav__saved');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    link.classList.remove('is-tapped');
    void link.offsetWidth; // restart the animation on a second tap
    link.classList.add('is-tapped');
  });
  // Coming back through the back button restores the page as it was left —
  // mid-animation — so the mark is cleared.
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.fv-bottom-nav .is-tapped').forEach(function (el) { el.classList.remove('is-tapped'); });
  });

  /* ------------------------------------------------------------ cart badge -- */
  /* The header bag shares data-fv-cart-count with every other count, which
     the cart code writes as text. "0" is a count nobody needs to read, so the
     badge hides itself whenever that is the text. */
  document.querySelectorAll('[data-fv-mhead-count]').forEach(function (badge) {
    function paint() {
      var n = parseInt(badge.textContent, 10);
      badge.hidden = !(n > 0);
    }
    paint();
    if (typeof MutationObserver === 'function') {
      new MutationObserver(paint).observe(badge, { childList: true, characterData: true, subtree: true });
    }
  });

  /* ------------------------------------------------------------ deliver to -- */
  var KEY = 'furniva:deliver-to';
  var sheet = document.querySelector('[data-fv-deliver]');
  var labels = document.querySelectorAll('[data-fv-deliver-label]');

  function recall() {
    try {
      var saved = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (saved && /^\d{6}$/.test(saved.pincode)) return saved;
    } catch (error) {}
    try {
      var legacy = sessionStorage.getItem('furniva:pincode');
      if (legacy && /^\d{6}$/.test(legacy)) return { pincode: legacy, city: null };
    } catch (error) {}
    return null;
  }

  function label(record) {
    if (!record) return 'Enter your pincode';
    return 'Deliver to ' + (record.city ? record.city + ' ' : '') + record.pincode;
  }

  function paintLabels(record) {
    labels.forEach(function (el) { el.textContent = label(record); });
  }

  function remember(pincode, place) {
    var record = { pincode: pincode, city: place && place.city ? String(place.city) : null, at: Date.now() };
    try { localStorage.setItem(KEY, JSON.stringify(record)); } catch (error) {}
    try { sessionStorage.setItem('furniva:pincode', pincode); } catch (error) {}
    paintLabels(record);
    try { document.dispatchEvent(new CustomEvent('furniva:deliver-to', { detail: record })); } catch (error) {}
    return record;
  }

  paintLabels(recall());
  // A check on the product page or in the cart updates the header.
  document.addEventListener('furniva:deliver-to', function (event) { paintLabels(event.detail); });

  if (!sheet) return;

  var form = sheet.querySelector('[data-fv-deliver-form]');
  var input = sheet.querySelector('[data-fv-deliver-input]');
  var submit = sheet.querySelector('[data-fv-deliver-submit]');
  var result = sheet.querySelector('[data-fv-deliver-result]');
  var endpoint = sheet.getAttribute('data-fv-deliver-endpoint');
  var opener = null;

  function say(tone, text) {
    result.innerHTML = '';
    if (!text) return;
    var note = document.createElement('div');
    note.className = 'fv-deliver__say fv-deliver__say--' + tone;
    note.textContent = text;
    result.appendChild(note);
  }

  function open(trigger) {
    opener = trigger || null;
    var saved = recall();
    input.value = saved ? saved.pincode : '';
    say('', '');
    if (typeof sheet.showModal === 'function') sheet.showModal();
    else sheet.setAttribute('open', '');
    root.classList.add('fv-chrome-lock');
    set(false);
    // After the sheet has moved into place, so the keyboard does not fight the
    // slide-up on iOS.
    setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); } }, 220);
  }

  /* The keyboard. A phone draws it over the page without moving anything
     fixed, so a sheet at the foot of the screen ends up under it. While the
     sheet is open, the part of the screen the keyboard covers is measured
     from the visual viewport and the panel is lifted by exactly that much
     (--fv-kb, eased in furniva-chrome.css). */
  var vv = window.visualViewport;
  function lift() {
    var covered = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0;
    sheet.style.setProperty('--fv-kb', covered + 'px');
  }
  if (vv) {
    vv.addEventListener('resize', function () { if (sheet.open) lift(); });
    vv.addEventListener('scroll', function () { if (sheet.open) lift(); });
  }

  function close() {
    if (typeof sheet.close === 'function' && sheet.open) sheet.close();
    else sheet.removeAttribute('open');
  }

  sheet.addEventListener('close', function () {
    sheet.style.setProperty('--fv-kb', '0px');
    root.classList.remove('fv-chrome-lock');
    if (opener && typeof opener.focus === 'function') opener.focus();
  });

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-fv-deliver-open]');
    if (trigger) { event.preventDefault(); open(trigger); return; }
    if (event.target.closest && event.target.closest('[data-fv-deliver-close]')) { close(); return; }
    // A tap on the backdrop — the dialog element itself, outside the panel.
    if (event.target === sheet) close();
  });

  input.addEventListener('input', function () {
    input.value = input.value.replace(/\D/g, '').slice(0, 6);
  });

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    var value = input.value.trim();
    if (!/^[1-9]\d{5}$/.test(value)) {
      say('error', 'Enter a 6-digit pincode.');
      input.focus();
      return;
    }
    if (!endpoint) { remember(value, null); close(); return; }

    submit.disabled = true;
    say('neutral', 'Checking ' + value + '…');

    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 12000) : null;

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ pincode: value, lines: [] }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error('lookup ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var record = remember(value, data.place);
        var where = (record.city ? record.city + ' ' : '') + value;
        if (data.serviceable === true) {
          var span = data.delivery_estimate ||
            (data.delivery_days_min && data.delivery_days_max
              ? data.delivery_days_min + '–' + data.delivery_days_max + ' calendar days'
              : '');
          say('yes', 'We deliver to ' + where + (span ? ' — free, ' + span + ' from dispatch.' : ', free.'));
          setTimeout(close, 1400);
        } else if (data.serviceable === false) {
          say('no', 'We do not deliver to ' + where + ' yet.');
        } else {
          say('neutral', 'Saved ' + where + '. We could not confirm delivery just now — the product page will check again.');
        }
      })
      .catch(function () {
        // Not a "no". Keep the pincode — it is what they typed — and say plainly
        // that the check did not happen.
        remember(value, null);
        say('neutral', 'Saved ' + value + '. We could not check it just now — the product page will try again.');
      })
      .then(function () {
        clearTimeout(timer);
        submit.disabled = false;
      });
  });
})();
